import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import * as React from 'react';

import { IconButton } from '../../atoms/IconButton';
import { cn } from '../../lib/utils';

export interface PaginationProps {
  /** 1-based current page index. */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  className?: string;
}

const ICON_CLASS = 'w-[13px] h-[13px]';
const BUTTON_CLASS =
  'w-[29px] h-[29px] border border-grey-100 disabled:border-transparent disabled:bg-grey-100';

/**
 * `Pagination` — First / Prev / Next / Last 32×32 IconButton trio + a
 * "Page N of M" indicator. Per Figma `289:46581`. 1-based pagination:
 * `page=1` disables First+Prev, `page=pageCount` disables Next+Last.
 *
 * Keyboard a11y: ArrowLeft / ArrowRight on the navigation root trigger
 * `onPrev` / `onNext` (no-op when on the bounding page).
 */
export function Pagination({
  page,
  pageCount,
  onFirst,
  onPrev,
  onNext,
  onLast,
  className,
}: PaginationProps) {
  const isFirst = page <= 1;
  const isLast = page >= pageCount;

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowLeft') {
        if (!isFirst) {
          event.preventDefault();
          onPrev();
        }
      } else if (event.key === 'ArrowRight') {
        if (!isLast) {
          event.preventDefault();
          onNext();
        }
      }
    },
    [isFirst, isLast, onPrev, onNext]
  );

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      onKeyDown={handleKeyDown}
      className={cn('inline-flex items-center gap-4', className)}
    >
      <span
        data-testid="pagination-indicator"
        aria-live="polite"
        aria-current="page"
        className="text-[11px] leading-[1.3] text-dawn-black tabular-nums"
      >
        {`Page ${page} of ${pageCount}`}
      </span>
      <div className="inline-flex items-center gap-2">
        <IconButton
          variant="on-light"
          size="lg"
          aria-label="First page"
          disabled={isFirst}
          onClick={onFirst}
          className={BUTTON_CLASS}
        >
          <ChevronsLeft className={ICON_CLASS} />
        </IconButton>
        <IconButton
          variant="on-light"
          size="lg"
          aria-label="Previous page"
          disabled={isFirst}
          onClick={onPrev}
          className={BUTTON_CLASS}
        >
          <ChevronLeft className={ICON_CLASS} />
        </IconButton>
        <IconButton
          variant="on-light"
          size="lg"
          aria-label="Next page"
          disabled={isLast}
          onClick={onNext}
          className={BUTTON_CLASS}
        >
          <ChevronRight className={ICON_CLASS} />
        </IconButton>
        <IconButton
          variant="on-light"
          size="lg"
          aria-label="Last page"
          disabled={isLast}
          onClick={onLast}
          className={BUTTON_CLASS}
        >
          <ChevronsRight className={ICON_CLASS} />
        </IconButton>
      </div>
    </nav>
  );
}

Pagination.displayName = 'Pagination';
